import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
    TextField,
    InputAdornment,
    Menu,
    ListItemIcon,
    Avatar,
    IconButton,
    Paper,
    ClickAwayListener
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import ClearIcon from '@mui/icons-material/Clear';
import SearchOffIcon from '@mui/icons-material/SearchOff';

// Component for handling conflict user rows with its own state
const ConflictUserRow = ({ user, index, hrList, roleOwnerList, fetchUARData, uarService, setSnackbarMessage, setSnackbarSeverity, setSnackbarOpen, appRecord }) => {
    const [selectedReviewer, setSelectedReviewer] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [displayOptions, setDisplayOptions] = useState([]);
    
    const buttonRef = useRef(null);
    const searchInputRef = useRef(null);
    
    const roleOwner = hrList.find(hr => hr.id === user.ROLE_OWNER);
    
    // Get user details from appRecord
    const userDetails = appRecord?.find(record => record.EMAIL_ADDRESS === user.EMAIL_ADDRESS);
    
    // Filter out the current user to prevent self-assignment
    const filteredOptions = roleOwnerList?.filter(
        option => option.email.toLowerCase() !== user.EMAIL_ADDRESS.toLowerCase()
    ) || [];
    
    // Update search results whenever search text changes
    useEffect(() => {
        if (!searchText.trim()) {
            setDisplayOptions(filteredOptions);
            return;
        }
        
        const searchLower = searchText.toLowerCase().trim();
        const filtered = filteredOptions.filter(option => 
            option.label.toLowerCase().includes(searchLower) || 
            option.email.toLowerCase().includes(searchLower)
        );
        
        setDisplayOptions(filtered);
    }, [searchText, filteredOptions]);
    
    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
        // Reset search text and display all options when opening
        setSearchText('');
        setDisplayOptions(filteredOptions);
    };
    
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    
    const handleOptionClick = (option) => {
        setSelectedReviewer(option);
        handleMenuClose();
    };
    
    const handleSearchChange = (event) => {
        setSearchText(event.target.value);
    };
    
    const clearSearch = () => {
        setSearchText('');
        searchInputRef.current?.focus();
    };
    
    // Focus search field when dropdown opens
    useEffect(() => {
        if (anchorEl && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current.focus();
            }, 100);
        }
    }, [anchorEl]);
    
    // Highlight matching text in search results
    const highlightMatch = (text, searchTerm) => {
        if (!searchTerm.trim()) return text;
        
        const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) => 
            regex.test(part) ? 
                <span key={index} style={{ backgroundColor: 'rgba(25, 118, 210, 0.15)', fontWeight: 'bold' }}>{part}</span> : 
                <span key={index}>{part}</span>
        );
    };
    
    const handleSaveReviewer = async () => {
        if (!selectedReviewer) return;
        
        setIsSaving(true);
        try {
            const update = {
                id: user.id,
                ROLE_OWNER: selectedReviewer.value,
                WITH_SOD_VIOLATION: false
            };
            
            await uarService.updateUARSODRecord(user.id, update);
            
            // Refetch the data
            await fetchUARData();
            
            setSnackbarMessage('Reviewer updated successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error updating reviewer:', error);
            setSnackbarMessage('Failed to update reviewer');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSaving(false);
        }
    };
    
    return (
        <TableRow sx={{ 
            '&:last-child td, &:last-child th': { border: 0 },
            bgcolor: index % 2 === 0 ? 'white' : 'rgba(0, 0, 0, 0.02)'
        }}>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            bgcolor: `hsl(${(index * 70) % 360}, 60%, 85%)`,
                            color: `hsl(${(index * 70) % 360}, 70%, 30%)`,
                            fontSize: 14,
                            fontWeight: 'bold'
                        }}
                    >
                        {userDetails?.FIRST_NAME?.charAt(0) || user.FIRST_NAME?.charAt(0) || ''}{userDetails?.LAST_NAME?.charAt(0) || user.LAST_NAME?.charAt(0) || ''}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {userDetails?.FIRST_NAME || user.FIRST_NAME} {userDetails?.LAST_NAME || user.LAST_NAME}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {user.EMAIL_ADDRESS}
                        </Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell>
                <Chip 
                    label={user.ROLE_NAME} 
                    size="small" 
                    sx={{ 
                        fontSize: '0.75rem',
                        bgcolor: 'primary.light', 
                        color: 'white'
                    }} 
                />
            </TableCell>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">{roleOwner?.FIRST_NAME} {roleOwner?.LAST_NAME}</Typography>
                    <Tooltip title="SOD Violation: Same as user">
                        <WarningAmberIcon fontSize="small" color="error" />
                    </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {roleOwner?.EMAIL_ADDRESS}
                </Typography>
            </TableCell>
            <TableCell sx={{ minWidth: 240 }}>
                <TextField
                    ref={buttonRef}
                    fullWidth
                    size="small"
                    placeholder="Select a new reviewer..."
                    value={selectedReviewer ? selectedReviewer.label : ''}
                    onClick={handleMenuOpen}
                    InputProps={{
                        readOnly: true,
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                        sx: {
                            cursor: 'pointer',
                            '&.MuiOutlinedInput-root': {
                                paddingRight: 1
                            }
                        },
                        endAdornment: (
                            <Button 
                                onClick={handleMenuOpen}
                                size="small"
                                sx={{
                                    minWidth: 'auto',
                                    p: 0.5,
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'rgba(25, 118, 210, 0.04)'
                                    }
                                }}
                            >
                                Change
                            </Button>
                        )
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: 'rgba(0, 0, 0, 0.12)',
                                borderRadius: 1.5
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(25, 118, 210, 0.5)'
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'primary.main',
                                borderWidth: 1.5
                            }
                        }
                    }}
                />
                
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                        elevation: 5,
                        sx: {
                            width: 340,
                            maxHeight: 380,
                            overflow: 'hidden',
                            mt: 1,
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                        }
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 1.5, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                        <TextField
                            inputRef={searchInputRef}
                            fullWidth
                            size="small"
                            autoFocus
                            placeholder="Search by name or email..."
                            value={searchText}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchText ? (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            edge="end" 
                                            size="small" 
                                            onClick={clearSearch}
                                            sx={{ p: 0.5 }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                sx: {
                                    fontSize: '0.875rem',
                                    py: 0.5
                                }
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.15)',
                                        borderRadius: 1.5
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                    }
                                }
                            }}
                        />
                        
                        {searchText && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                                Found {displayOptions.length} matching reviewer{displayOptions.length !== 1 ? 's' : ''}
                            </Typography>
                        )}
                    </Box>
                    
                    {displayOptions.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <SearchOffIcon sx={{ color: 'text.secondary', fontSize: 40, opacity: 0.5, mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                No matching reviewers found
                            </Typography>
                            {searchText && (
                                <Button 
                                    size="small" 
                                    onClick={clearSearch} 
                                    sx={{ mt: 1, textTransform: 'none' }}
                                >
                                    Clear search
                                </Button>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ maxHeight: 280, overflow: 'auto', pt: 1, pb: 1 }}>
                            {displayOptions.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    onClick={() => handleOptionClick(option)}
                                    selected={selectedReviewer?.value === option.value}
                                    sx={{ 
                                        py: 1,
                                        px: 1.5,
                                        mx: 0.5,
                                        borderRadius: 1,
                                        mb: 0.5,
                                        '&:hover': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(25, 118, 210, 0.12)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.2)'
                                            }
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        <Avatar
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                bgcolor: `hsl(${(option.value * 45) % 360}, 70%, 45%)`,
                                                fontSize: 14,
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {(option.label.split(' ')[0]?.[0] || '') + (option.label.split(' ')[1]?.[0] || '')}
                                        </Avatar>
                                    </ListItemIcon>
                                    <Box sx={{ ml: 1, overflow: 'hidden' }}>
                                        <Typography variant="body2" fontWeight="500" noWrap>
                                            {searchText ? 
                                                highlightMatch(option.label.split('(')[0].trim(), searchText) : 
                                                option.label.split('(')[0].trim()
                                            }
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {searchText ? 
                                                highlightMatch(option.email, searchText) : 
                                                option.email
                                            }
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Box>
                    )}
                </Menu>
            </TableCell>
            <TableCell align="right">
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={!selectedReviewer || isSaving}
                    onClick={handleSaveReviewer}
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                    sx={{ 
                        borderRadius: 6,
                        px: 2,
                        py: 0.75,
                        textTransform: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                        fontWeight: 500,
                        fontSize: '0.8125rem',
                        lineHeight: '1.5',
                        letterSpacing: '0.02em',
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: 'primary.main',
                        '&:hover': {
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                            transform: 'translateY(-1px)',
                            backgroundColor: 'primary.dark'
                        },
                        '&:active': {
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                            transform: 'translateY(1px)'
                        },
                        '&.Mui-disabled': {
                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                            color: 'rgba(0, 0, 0, 0.26)'
                        }
                    }}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </Button>
            </TableCell>
        </TableRow>
    );
};

export default ConflictUserRow; 